// Per spec §3.3 + §7.6: localStorage-backed cookie for returning visitors.
// 30-day expiry. Per-origin via localStorage (no cross-vendor leakage).

import { useCallback } from 'react'

const KEY = 'mikla_chat_lead'
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export interface LeadCookie {
  name: string
  email: string
  // Direction B "Ink Header" 4-field welcome: phone is captured + cached so a
  // returning visitor's autosubmit can re-pass it to the backend for SMS
  // followup eligibility (once backend wires persistence).
  phone?: string
  event_type?: string
  cached_at: number
}

export interface LeadCookieAPI {
  read: () => LeadCookie | null
  write: (data: { name: string; email: string; phone?: string; event_type?: string }) => void
  clear: () => void
}

export function useLeadCookie(): LeadCookieAPI {
  const read = useCallback((): LeadCookie | null => {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as LeadCookie
      const age = Date.now() - (parsed.cached_at || 0)
      if (age > MAX_AGE_MS) return null
      return parsed
    } catch {
      localStorage.removeItem(KEY)
      return null
    }
  }, [])

  const write = useCallback(
    (data: { name: string; email: string; phone?: string; event_type?: string }) => {
      const payload: LeadCookie = { ...data, cached_at: Date.now() }
      localStorage.setItem(KEY, JSON.stringify(payload))
    },
    [],
  )

  const clear = useCallback(() => {
    localStorage.removeItem(KEY)
  }, [])

  return { read, write, clear }
}
